import React, { useMemo } from "react";
import type { Dayjs } from "dayjs";
import { DateUtils } from "utils/dateutils";
import {
  computeDecorators,
  computeVisulisationLayout,
  computeXPositionsForDatesv6
} from "../../../compare-schedule-2/TemporalLayout";
import {
  computeTimelineDataForSubmittal,
  computeTimelineDataMaterial
} from "../../utils";
import { TActivitiesV3, TLinkedMaterial, TLinkedSubmittal } from "../../models";
import { TimelineLayoutDefs, renderTimelineLayout } from "./layout-renderer";

type SubmittalProps = {
  kind: "submittal";
  submittal: TLinkedSubmittal;
  activityComparison: TActivitiesV3;
  projectId: string;
};

type MaterialProps = {
  kind: "material";
  material: TLinkedMaterial;
  activityComparison: TActivitiesV3;
  projectId: string;
};

type Props = SubmittalProps | MaterialProps;

const BLOCK_GAP = 16;
const HEADING_FS = 13;
const HEADING_HEIGHT = HEADING_FS + 8;
// Post-track date label sits 14 px below the bar (single line, ~12 px
// font). TemporalLayout's PADDING covers most of that; this buffer keeps
// the descender inside the colored block on raster.
const BLOCK_BOTTOM_BUFFER = 18;

// Single-SVG Before+After timeline so today/FDD line up across both
// halves automatically and the whole row rasterizes in one pass — key
// to staying under DOM-node + raster-call budgets at 2000+ row scale.
function CombinedTimelineDiffInner(props: Props) {
  const { activityComparison, projectId } = props;

  const dataForBefore = useMemo(
    () =>
      props.kind === "submittal"
        ? computeTimelineDataForSubmittal(
            "before",
            props.submittal,
            activityComparison,
            projectId
          )
        : computeTimelineDataMaterial(
            "before",
            props.material,
            activityComparison,
            projectId
          ),

    [props, activityComparison, projectId]
  );

  const dataForAfter = useMemo(
    () =>
      props.kind === "submittal"
        ? computeTimelineDataForSubmittal(
            "after",
            props.submittal,
            activityComparison,
            projectId
          )
        : computeTimelineDataMaterial(
            "after",
            props.material,
            activityComparison,
            projectId
          ),

    [props, activityComparison, projectId]
  );

  const xLocationsByDate = useMemo(() => {
    const dates: Dayjs[] = [
      ...dataForBefore.allDates,
      ...dataForAfter.allDates
    ];
    if (props.kind === "submittal") {
      const pushDates = (list: typeof props.submittal.oldGoverningMaterials) =>
        list.forEach((gm) => {
          dates.push(DateUtils.dateTimeObj(gm.start_date));
          dates.push(DateUtils.dateTimeObj(gm.end_date));
        });
      pushDates(props.submittal.oldGoverningMaterials);
      pushDates(props.submittal.newGoverningMaterials);
    }
    return computeXPositionsForDatesv6(
      dates,
      dataForAfter.timelineData.today.date
    );
  }, [props, dataForBefore.allDates, dataForAfter]);

  const beforeLayout = useMemo(
    () =>
      computeVisulisationLayout(
        projectId,
        dataForBefore.timelineData.primaryElement.label,
        xLocationsByDate,
        Object.values(dataForBefore.timelineData.primaryElement.milestones),
        dataForBefore.timelineData.siCalculations,
        dataForBefore.timelineData.today.date,
        props.kind === "submittal" ? props.submittal.oldGoverningMaterials : []
      ),

    [props, projectId, dataForBefore, xLocationsByDate]
  );

  const afterLayout = useMemo(
    () =>
      computeVisulisationLayout(
        projectId,
        dataForAfter.timelineData.primaryElement.label,
        xLocationsByDate,
        Object.values(dataForAfter.timelineData.primaryElement.milestones),
        dataForAfter.timelineData.siCalculations,
        dataForAfter.timelineData.today.date,
        props.kind === "submittal" ? props.submittal.newGoverningMaterials : []
      ),

    [props, projectId, dataForAfter, xLocationsByDate]
  );

  const arrow = useMemo(() => {
    const beforeLastMaterialROJ =
      props.kind === "submittal" &&
      props.submittal.oldGoverningMaterials.length > 0
        ? props.submittal.oldGoverningMaterials[
            props.submittal.oldGoverningMaterials.length - 1
          ].end_date
        : null;
    const afterLastMaterialROJ =
      props.kind === "submittal" &&
      props.submittal.newGoverningMaterials.length > 0
        ? props.submittal.newGoverningMaterials[
            props.submittal.newGoverningMaterials.length - 1
          ].end_date
        : null;
    const beforeFDD =
      dataForBefore.timelineData.primaryElement.milestones.final_deadline;
    const afterFDD =
      dataForAfter.timelineData.primaryElement.milestones.final_deadline;
    return computeDecorators(
      xLocationsByDate,
      beforeLastMaterialROJ,
      afterLastMaterialROJ,
      beforeFDD ? beforeFDD.normalizedDate : null,
      afterFDD ? afterFDD.normalizedDate : null
    ).arrow;
  }, [props, xLocationsByDate, dataForBefore, dataForAfter]);

  const isCompletedWorkflow =
    props.kind === "submittal"
      ? props.submittal.workflow_status === "COMPLETED"
      : props.material.workflow_status === "COMPLETED";

  if (beforeLayout.layoutError || afterLayout.layoutError) {
    return <div>⚠ {beforeLayout.layoutError || afterLayout.layoutError}</div>;
  }

  const width = Math.max(beforeLayout.width, afterLayout.width);
  const beforeBlockHeight =
    HEADING_HEIGHT + beforeLayout.height + BLOCK_BOTTOM_BUFFER;
  const afterBlockHeight =
    HEADING_HEIGHT + afterLayout.height + BLOCK_BOTTOM_BUFFER;
  const totalHeight = beforeBlockHeight + BLOCK_GAP + afterBlockHeight + 4;

  const afterBlockY = beforeBlockHeight + BLOCK_GAP;

  return (
    <svg
      viewBox={`0 0 ${width} ${totalHeight}`}
      width={width}
      height={totalHeight}
      preserveAspectRatio="xMinYMin meet"
      textRendering="geometricPrecision"
      className="block max-w-full h-auto"
    >
      {/*
        Inline font stack — the rasterize pipeline loads the SVG via a
        blob URL, which renders in a sandboxed context that can't see
        the parent document's web fonts.
      */}
      <style>
        {
          'text { font-family: "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }'
        }
      </style>
      <TimelineLayoutDefs />

      {/* Before block */}
      <rect
        x={0}
        y={0}
        width={width}
        height={beforeBlockHeight}
        fill="#f9fafb"
      />
      <text x={4} y={HEADING_FS + 2} fontSize={HEADING_FS} fill="#6b7280">
        Before
      </text>
      <g transform={`translate(0, ${HEADING_HEIGHT})`}>
        {renderTimelineLayout(beforeLayout, {
          isCompletedWorkflow,
          keyPrefix: "b-"
        })}
      </g>

      {/* After block */}
      <rect
        x={0}
        y={afterBlockY}
        width={width}
        height={afterBlockHeight}
        fill="#eff6ff"
      />
      <text
        x={4}
        y={afterBlockY + HEADING_FS + 2}
        fontSize={HEADING_FS}
        fill="#1e40af"
      >
        After
      </text>
      <g transform={`translate(0, ${afterBlockY + HEADING_HEIGHT})`}>
        {renderTimelineLayout(afterLayout, {
          arrow,
          isCompletedWorkflow,
          keyPrefix: "a-"
        })}
      </g>
    </svg>
  );
}

export const CombinedTimelineDiff = React.memo(CombinedTimelineDiffInner);
